import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col } from "reactstrap";
import { CardElement, Elements, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { getProfileData, updateProfileSubscriptionStatus } from '../../redux/actions/profileData';
import { getProfileEditData } from '../../redux/actions/profileEditData';

import Main from '../../layout/main';
import API from '../../API';

// Stripe configuration
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51QCHQaQf6Dvh5chGaxdbQkwBsI2KbOCvjK6PyymADHu0LzMFCnlQ09LkiGjqCYlcvQNoh10ooyywNPG9NTR7BrEB00HASLZHPH");

const BecomeProPage = () => {
    const [walletAmount, setWalletAmount] = useState(null);
    const [availableCards, setAvailableCards] = useState([]);
    const [error, setError] = useState('');
    const history = useHistory();
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [cardHolder, setCardHolder] = useState('');
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [loadingShow, setLoadingShow] = useState(false);
    const [cardError, setCardError] = useState('');
    const [cardComplete, setCardComplete] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [plan, setPlan] = useState('yearly');
    const [packageTiers, setPackageTiers] = useState([]);
    const [tiersLoading, setTiersLoading] = useState(true);
    const [selectedTierId, setSelectedTierId] = useState(null);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showAddCard, setShowAddCard] = useState(false);
    const [currentPlanIndex, setCurrentPlanIndex] = useState(0);
    const [addingCard, setAddingCard] = useState(false);

    const handleCardChange = (event) => {
        if (event.error) {
            setCardError(event.error.message);
        } else {
            setCardError('');
        }
        setCardComplete(event.complete);
    };

    const handleAddCardSuccess = async () => {
        await fetchAvailableCards();
        setShowAddCard(false);
        setCardHolder('');
        setCardComplete(false);
        setCardError('');
        setError('');
        if (elements) {
            const cardElement = elements.getElement(CardElement);
            if (cardElement) cardElement.clear();
        }
        // Show success message
        setErrorMessage('Card added successfully!');
        setShowErrorAlert(true);
    };

    const handleAddCardClose = () => {
        setShowAddCard(false);
        setCardHolder('');
        setCardComplete(false);
        setCardError('');
        if (elements) {
            const cardElement = elements.getElement(CardElement);
            if (cardElement) cardElement.clear();
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) {
            setError('Stripe is not loaded yet.');
            return;
        }
        if (!cardHolder) {
            setError('Please enter the cardholder name.');
            return;
        }
        if (!cardComplete) {
            setError('Please complete your card details.');
            return;
        }
        if (cardError !== '') {
            setError(cardError);
            return;
        }
        setError('');
        setLoadingShow(true);
        try {
            const cardElement = elements.getElement(CardElement);
            const paymentMethod = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
                billing_details: {
                    name: cardHolder,
                },
            });
            if (paymentMethod.error) {
                setError(paymentMethod.error.message);
                setLoadingShow(false);
                return;
            }
            addCardToAccount(paymentMethod.paymentMethod.id);
        } catch (err) {
            setLoadingShow(false);
            setError('An error occurred. Please try again.');
        }
    };

    const addCardToAccount = async (paymentMethodId) => {
        setLoadingShow(true);
        setAddingCard(true);
        try {
            const data = {
                payment_method: paymentMethodId,
                user_id: Cookies.get('provider_id'),
            };
            
            const response = await axios.post(API.baseURL + `/v2/providers/add_card_to_stripe`, data, API.options);
            if (response.status === 200) {
                await fetchAvailableCards();
                handleAddCardSuccess();
                setError('');
            } else {
                setError('Failed to add card. Please try again.');
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "Failed to add card. Please try again.";
            setErrorMessage(errorMsg);
            setShowErrorAlert(true);
            setError("");
        } finally {
            setLoadingShow(false);
            setAddingCard(false);
        }
    };

    const confirmPayment = async (cardId) => {
        setLoadingShow(true);
        setAddingCard(false); // Ensure we're not in card adding mode
        
        if (!plan) {
            setError("Please select a billing cycle");
            setLoadingShow(false);
            return;
        }
        
        if (!selectedTierId) {
            setError("Please select a plan tier");
            setLoadingShow(false);
            return;
        }

        if (!cardId) {
            setError("Please select a payment method");
            setLoadingShow(false);
            return;
        }
        
        const data = {
            payment_method: cardId,
            user_id: Cookies.get('provider_id'),
            plan: plan,
            package_id: selectedTierId
        };
        
        try {
            const response = await axios.post(API.baseURL + `/v2/providers/upgrade_to_pro`, data, API.options);
            console.log('Upgrade API response:', response.data);
            
            if (response.status === 200) {
                console.log('Upgrade successful, updating profile data...');
                
                // Immediately update the profile subscription status
                dispatch(updateProfileSubscriptionStatus(true));
                
                // Refresh profile data from API to ensure consistency
                dispatch(getProfileData());
                dispatch(getProfileEditData());
                
                setPaymentSuccess(true);
                setLoadingShow(false);
            } else {
                setLoadingShow(false);
            }
        } catch (error) {
            setLoadingShow(false);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || "Upgrade failed. Please try again.";
            setErrorMessage(errorMsg);
            setShowErrorAlert(true);
            setError("");
        }
    };

    const fetchAvailableCards = async () => {
        try {
            const response = await axios.get(API.baseURL + `/providers/wallet_get_provider_card_details`, API.options);
            if (response.data.code === 200) {
                const cards = response.data.data.stripe_card_details || [];
                setAvailableCards(cards);
                if (cards.length > 0 && !selectedCardId) {
                    setSelectedCardId(cards[0].id);
                }
            } else {
                setError(response.data.message);
            }
            setLoading(false);
        } catch (err) {
            console.error("API Error:", err);
            setLoading(false);
        }
    };

    const fetchPackageTiers = async () => {
        try {
            const response = await axios.get(API.baseURL + `/v2/providers/pro_plans`, API.options);
            if (response.status === 200 && response.data) {
                setPackageTiers(response.data);
                if (response.data.length > 0) {
                    setSelectedTierId(response.data[0].id);
                }
            }
        } catch (err) {
            console.error("Failed to fetch package tiers:", err);
        } finally {
            setTiersLoading(false);
        }
    };

    const fetchWalletAmount = async () => {
        try {
            const response = await axios.get(API.baseURL + `/v2/providers/wallets`, API.options);
            const walletData = response.data;
            setWalletAmount(walletData.amount);
            fetchAvailableCards();
        } catch (err) {
            console.error("Failed to fetch wallet amount", err);
        }
    };

    useEffect(() => {
        fetchWalletAmount();
        fetchPackageTiers();
    }, []);

    useEffect(() => {
        const selectedTier = packageTiers.find(t => t.id === selectedTierId);
        if (selectedTier) {
            const showMonthly = selectedTier.allow_monthly;
            const showYearly = selectedTier.allow_yearly;
            
            if (plan === 'monthly' && !showMonthly) {
                setPlan(showYearly ? 'yearly' : 'monthly');
            } else if (plan === 'yearly' && !showYearly) {
                setPlan(showMonthly ? 'monthly' : 'yearly');
            }
        }
    }, [selectedTierId, packageTiers, plan]);

    const handlePaymentSuccess = () => {
        setPaymentSuccess(false);
        
        console.log('Payment successful, updating subscription status...');
        
        // Immediately update the profile subscription status
        dispatch(updateProfileSubscriptionStatus(true));
        
        // Also refresh profile data from API to ensure consistency
        dispatch(getProfileData());
        dispatch(getProfileEditData());
        
        // Force a page reload after a short delay to ensure header updates
        setTimeout(() => {
            console.log('Redirecting after payment success...');
            const selectedTier = packageTiers.find(t => t.id === selectedTierId);
            
            if (selectedTier && selectedTier.skip_wallet === true) {
                history.push('/dashboard');
                window.location.reload();
            } else {
                history.push('/add-funds');
                window.location.reload();
            }
        }, 1500);
    };

    return (
        <div>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Become a Pro - EasyBids</title>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"></link>
            </Helmet>
            <Main additionalClass_main="ebw-pro__main">
                <section className="ebw-become-pro__section">
                    <Container className="ebw-become-pro__container">
                        <div className="ebw-become-pro__content">
                            {/* Plan Selection */}
                            <div className="ebw-become-pro__main-section">
                                <div className="ebw-become-pro__title-section">
                                    <h1 className="ebw-become-pro__title" style={{textAlign: 'left'}}>
                                        Choose a <span className="ebw-become-pro__title-highlight">Plan</span>
                                    </h1>
                                    <p className="ebw-become-pro__subtitle" style={{textAlign: 'left'}}>
                                        Become a Pro+ member now and boost your business!
                                    </p>
                                </div>
                                
                                {tiersLoading ? (
                                    <div className="ebw-become-pro__loading">
                                        <div>Loading plans...</div>
                                    </div>
                                ) : packageTiers.length > 0 ? (
                                    <>
                                        <div className="ebw-become-pro__plans-grid">
                                            {packageTiers.map((tier, index) => (
                                                <div 
                                                    key={tier.id}
                                                    className={`ebw-become-pro__plan-card ${selectedTierId === tier.id ? 'ebw-become-pro__plan-card--selected' : ''}`}
                                                    onClick={() => setSelectedTierId(tier.id)}
                                                >
                                                    <div className="ebw-become-pro__plan-header">
                                                        {tier.allow_yearly && (
                                                            <div className="ebw-become-pro__plan-crown">
                                                                <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <g clipPath="url(#clip0_992_535)">
                                                                        <path d="M2.10001 4.40037L5.25001 6.50037L8.38951 2.10537C8.45888 2.00814 8.55048 1.9289 8.65667 1.87422C8.76286 1.81954 8.88057 1.79102 9.00001 1.79102C9.11944 1.79102 9.23716 1.81954 9.34335 1.87422C9.44953 1.9289 9.54113 2.00814 9.61051 2.10537L12.75 6.50037L15.9 4.40037C16.0192 4.32105 16.1587 4.27752 16.3018 4.27492C16.445 4.27232 16.5859 4.31075 16.7079 4.38568C16.8299 4.46061 16.928 4.56891 16.9904 4.69777C17.0528 4.82663 17.0771 4.97067 17.0603 5.11287L15.828 15.5881C15.8065 15.7706 15.7188 15.9388 15.5815 16.0608C15.4443 16.1829 15.267 16.2503 15.0833 16.2504H2.91676C2.73306 16.2503 2.55575 16.1829 2.41848 16.0608C2.2812 15.9388 2.1935 15.7706 2.17201 15.5881L0.939756 5.11212C0.923106 4.96998 0.947469 4.82604 1.00997 4.6973C1.07247 4.56856 1.1705 4.46039 1.29249 4.38556C1.41448 4.31073 1.55532 4.27236 1.69841 4.27498C1.8415 4.27761 1.98084 4.32111 2.10001 4.40037ZM9.00001 11.7504C9.39783 11.7504 9.77936 11.5923 10.0607 11.311C10.342 11.0297 10.5 10.6482 10.5 10.2504C10.5 9.85254 10.342 9.47101 10.0607 9.18971C9.77936 8.9084 9.39783 8.75037 9.00001 8.75037C8.60218 8.75037 8.22065 8.9084 7.93935 9.18971C7.65804 9.47101 7.50001 9.85254 7.50001 10.2504C7.50001 10.6482 7.65804 11.0297 7.93935 11.311C8.22065 11.5923 8.60218 11.7504 9.00001 11.7504Z" fill="currentColor"/>
                                                                    </g>
                                                                    <defs>
                                                                        <clipPath id="clip0_992_535">
                                                                            <rect width="18" height="18" fill="white" transform="translate(0 0.5)"/>
                                                                        </clipPath>
                                                                    </defs>
                                                                </svg>
                                                            </div>
                                                        )}
                                                        <h4 className="ebw-become-pro__plan-name">{tier.title}</h4>
                                                    </div>
                                                    <div className="ebw-become-pro__plan-price">${tier.monthly_price?tier.monthly_price:tier.yearly_price}/{tier.allow_monthly?'month':'year'}</div>
                                                    <div className="ebw-become-pro__plan-divider"></div>
                                                    <ul className="ebw-become-pro__plan-features">
                                                        {tier.features && tier.features.length > 0 ? (
                                                            tier.features.map((feature, featureIndex) => (
                                                                <li key={featureIndex}>{feature}</li>
                                                            ))
                                                        ) : (
                                                            <li>Pro features included</li>
                                                        )}
                                                    </ul>
                                                    <button 
                                                        className="ebw-become-pro__plan-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedTierId(tier.id);
                                                        }}
                                                    >
                                                        Select Plan
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : (
                                    <div className="ebw-become-pro__no-plans">
                                        <div>No plans available</div>
                                        <div>Please try again later</div>
                                    </div>
                                )}
                            </div>

                            {/* Billing Cycle and Payment Method Section */}
                            <div className="ebw-become-pro__billing-payment-section">
                                {/* Billing Cycle Card */}
                                {(() => {
                                    const selectedTier = packageTiers.find(t => t.id === selectedTierId);
                                    const showMonthly = selectedTier?.allow_monthly;
                                    const showYearly = selectedTier?.allow_yearly;
                                    
                                    if (!showMonthly && !showYearly) {
                                        return null;
                                    }

                                    return (
                                        <div className="ebw-become-pro__billing-card">
                                            {/* <h3 className="ebw-become-pro__section-title">Billing Cycle</h3> */}
                                            {showMonthly && (
                                                <div className="ebw-become-pro__billing-option">
                                                    <div className="ebw-become-pro__billing-option-content">
                                                        <div className="ebw-become-pro__billing-option-left">
                                                            <input
                                                                type="radio"
                                                                id="monthly"
                                                                name="billing"
                                                                value="monthly"
                                                                checked={plan === 'monthly'}
                                                                onChange={() => setPlan('monthly')}
                                                                style={{
                                                                    alignSelf: 'center',
                                                                    marginTop: '0',
                                                                    marginBottom: '0',
                                                                    width: '26px',
                                                                    height: '26px'
                                                                }}
                                                            />
                                                            <div className="ebw-become-pro__billing-label-price">
                                                                <label htmlFor="monthly">Monthly</label>
                                                                {selectedTier && (
                                                                    <div className="ebw-become-pro__billing-price">
                                                                        <span className="ebw-become-pro__billing-price--simple">
                                                                            <span className="price-amount">${selectedTier.monthly_price}</span>/month
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {/* Separator - only show when both options are available */}
                                            {showMonthly && showYearly && (
                                                <div className="ebw-become-pro__billing-separator"></div>
                                            )}
                                            
                                            {showYearly && (
                                                <div className="ebw-become-pro__billing-option">
                                                    <div className="ebw-become-pro__billing-option-content">
                                                        <div className="ebw-become-pro__billing-option-left">
                                                            <input
                                                                type="radio"
                                                                id="yearly"
                                                                name="billing"
                                                                value="yearly"
                                                                checked={plan === 'yearly'}
                                                                onChange={() => setPlan('yearly')}
                                                                style={{
                                                                    alignSelf: 'center',
                                                                    marginTop: '0',
                                                                    marginBottom: '0',
                                                                    width: '26px',
                                                                    height: '26px'
                                                                }}
                                                            />
                                                            <div className="ebw-become-pro__billing-label-price" style={{position:'relative'}}>
                                                                <label htmlFor="yearly">Yearly</label>
                                                                {selectedTier && (
                                                                    <div className="ebw-become-pro__billing-price">
                                                                        {(() => {
                                                                            const yearlyTotal = selectedTier.discounted_annual_price?selectedTier.annual_price:selectedTier.yearly_price;
                                                                            const discountPercentage = parseFloat(selectedTier.discounted_annual_price);
                                                                            return (
                                                                                <>
                                                                                    {discountPercentage > 0 ? (
                                                                                        <div className="ebw-become-pro__billing-price-row">
                                                                                            <span className="ebw-become-pro__billing-price--old">
                                                                                                <span className="price-amount" style={{textDecoration: 'line-through'}}>${yearlyTotal}</span><span style={{textDecoration: 'line-through'}}>/year</span>
                                                                                            </span>
                                                                                            <span className="ebw-become-pro__billing-price--new">
                                                                                                <span className="price-amount">${Math.floor(selectedTier.discounted_annual_price)}</span>/year
                                                                                            </span>
                                                                                            <span className="ebw-become-pro__billing-price--discount" style={{position:'absolute',right:0,top:0,color: '#3D84FB', backgroundColor:'#EFF6FF',borderRadius:'50px',padding:'4px 8px'}}>
                                                                                                <span className="price-amount">Save ${Math.floor(selectedTier.total_discount_value)}</span>
                                                                                            </span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="ebw-become-pro__billing-price--simple">
                                                                                            <span className="price-amount">${yearlyTotal}</span>/year
                                                                                        </span>
                                                                                    )}
                                                                                </>
                                                                            );
                                                                        })()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {selectedTier && (() => {
                                                        const discountPercentage = parseFloat(selectedTier.annual_price_discount);
                                                        return discountPercentage > 0 ? (
                                                            <div className="ebw-become-pro__save-badge">Save ${Math.round(discountPercentage)}</div>
                                                        ) : null;
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* Payment Method Card */}
                                <div className="ebw-become-pro__payment-card">
                                    {/* <h3 className="ebw-become-pro__section-title">Payment Method</h3> */}
                                    {availableCards.length > 0 ? (
                                        <div>
                                            {availableCards.map((card) => (
                                                <div
                                                    key={card.id}
                                                    className={`ebw-become-pro__payment-method ${selectedCardId === card.id ? 'ebw-become-pro__payment-method--selected' : ''}`}
                                                    onClick={() => setSelectedCardId(card.id)}
                                                >
                                                    <div className="ebw-become-pro__card-info">
                                                        <div className="ebw-become-pro__card-number">{`•••• ${card.last4_digit}`}</div>
                                                        <div className="ebw-become-pro__card-expiry">{`Expires ${card.exp_month < 10 ? '0' + card.exp_month : card.exp_month}/${String(card.exp_year).slice(-2)}`}</div>
                                                    </div>
                                                    {selectedCardId === card.id ? (
                                                        <div className="ebw-become-pro__card-check">✓</div>
                                                    ) : (
                                                        <div className="ebw-become-pro__card-arrow">
                                                            <i className="fa fa-chevron-right"></i>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            <div
                                                className="ebw-become-pro__add-card"
                                                onClick={() => setShowAddCard(true)}
                                            >
                                                <i className="fa fa-plus"></i>
                                                <span style={{ color: '#171717'}}>Add card</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="ebw-become-pro__no-cards">
                                            <p>No payment methods added.</p>
                                            <div
                                                className="ebw-become-pro__add-card"
                                                onClick={() => setShowAddCard(true)}
                                            >
                                                <i className="fa fa-plus"></i>
                                                <span>Add Card</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Add Card Modal */}
                            {showAddCard && (
                                <div className="ebw-become-pro__modal-overlay">
                                    <div className="ebw-become-pro__modal">
                                        <div className="ebw-become-pro__modal-header">
                                            <h3>Add New Card</h3>
                                            <button onClick={handleAddCardClose} className="ebw-become-pro__modal-close">
                                                <i className="fa fa-times"></i>
                                            </button>
                                        </div>
                                        <form onSubmit={handleSubmit} className="ebw-become-pro__modal-form">
                                            <div className="ebw-become-pro__form-group">
                                                <label>Cardholder Name</label>
                                                <input
                                                    type="text"
                                                    value={cardHolder}
                                                    onChange={(e) => setCardHolder(e.target.value)}
                                                    placeholder="Enter cardholder name"
                                                    required
                                                    disabled={loadingShow}
                                                />
                                            </div>
                                            <div className="ebw-become-pro__form-group">
                                                <label>Card Details</label>
                                                <div className="ebw-become-pro__card-element-container">
                                                    <CardElement
                                                        options={{
                                                            style: {
                                                                base: {
                                                                    fontSize: '16px',
                                                                    color: '#424770',
                                                                    '::placeholder': {
                                                                        color: '#aab7c4',
                                                                    },
                                                                },
                                                            },
                                                            disabled: loadingShow,
                                                        }}
                                                        onChange={handleCardChange}
                                                    />
                                                </div>
                                                {cardError && <div className="ebw-become-pro__error">{cardError}</div>}
                                            </div>
                                            <div className="ebw-become-pro__modal-actions">
                                                <button 
                                                    type="button" 
                                                    onClick={handleAddCardClose} 
                                                    className="ebw-become-pro__btn-secondary"
                                                    disabled={loadingShow}
                                                >
                                                    Cancel
                                                </button>
                                                <button 
                                                    type="submit" 
                                                    disabled={!cardComplete || !cardHolder || loadingShow} 
                                                    className="ebw-become-pro__btn-primary"
                                                >
                                                    {loadingShow ? (addingCard ? 'Adding Card...' : 'Processing...') : 'Add Card'}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="ebw-become-pro__actions">
                                <button 
                                    className="ebw-become-pro__confirm-btn"
                                    disabled={availableCards.length === 0 || !selectedCardId || loadingShow}
                                    onClick={() => {
                                        if (availableCards.length > 0) {
                                            if (!selectedCardId) {
                                                setError('Please select a card.');
                                                return;
                                            }
                                            setError('');
                                            confirmPayment(selectedCardId);
                                        } else {
                                            setError('Please add a payment method first.');
                                        }
                                    }}
                                >
                                    {loadingShow ? 'Processing Upgrade...' : 'CONFIRM DETAILS'}
                                </button>
                                
                                <button className="ebw-become-pro__continue-free-btn" onClick={() => history.push('/add-funds')}>
                                    Continue with Free Plan
                                </button>
                            </div>

                            {/* Error Display */}
                            {error && (
                                <div className="ebw-become-pro__error-message">
                                    {error}
                                </div>
                            )}

                            {/* Success Modal */}
                            {paymentSuccess && (
                                <div className="ebw-become-pro__modal-overlay">
                                    <div className="ebw-become-pro__modal ebw-become-pro__modal--success">
                                        <div className="ebw-become-pro__modal-header">
                                            <h3>Upgrade to Pro+ Successfully!</h3>
                                        </div>
                                        <div className="ebw-become-pro__modal-body">
                                            <p>Your subscription has been activated successfully.</p>
                                        </div>
                                        <div className="ebw-become-pro__modal-actions">
                                            <button onClick={handlePaymentSuccess} className="ebw-become-pro__btn-primary">
                                                OK
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Error/Success Alert */}
                            {showErrorAlert && (
                                <div className="ebw-become-pro__modal-overlay">
                                    <div className={`ebw-become-pro__modal ${errorMessage.includes('successfully') ? 'ebw-become-pro__modal--success' : 'ebw-become-pro__modal--error'}`}>
                                        <div className="ebw-become-pro__modal-header">
                                            <h3>{errorMessage.includes('successfully') ? 'Success' : 'Error'}</h3>
                                        </div>
                                        <div className="ebw-become-pro__modal-body">
                                            <p>{errorMessage}</p>
                                        </div>
                                        <div className="ebw-become-pro__modal-actions">
                                            <button onClick={() => setShowErrorAlert(false)} className="ebw-become-pro__btn-primary">
                                                OK
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Container>
                </section>
            </Main>
        </div>
    );
};

const BecomePro = () => {
    return (
        <Elements stripe={stripePromise}>
            <BecomeProPage />
        </Elements>
    );
};

export default BecomePro; 
